// @flow

import { pipe, map, filter } from 'wonka';
import { CombinedError } from 'urql';
import type { Exchange } from 'urql';

import makeGraphQlCall, { type Operation, type RequestContext } from '@ta/platform.graphql';

const queue = [];
const cache = {};

const ssrFetchExchange: Exchange = () => ops$ =>
  pipe(
    ops$,
    map(op => {
      if (!cache[op.key]) {
        // urql uses the operation type as the operation name, which produces
        // an invalid request. However, a null operation name is permitted.
        queue.push({ ...op, operationName: null });
        return null;
      }
      return { operation: op, ...cache[op.key] };
    }),
    filter(op => op !== null),
  );

export default ssrFetchExchange;

/**
 * Fulfill all queued requests with the given data.
 */
export const fulfillData = (results: Array<any>, extensions: any) => {
  results.forEach((result, idx) => {
    const { key } = (queue[idx]: any);
    cache[key] = {
      ...result,
      errors: undefined,
      error: Array.isArray(result.errors)
        ? new CombinedError({
            graphQLErrors: result.errors,
          })
        : undefined,
    };
  });

  cache.extensions = extensions;

  return results;
};

/**
 * Fulfill all queued requests by calling makeGraphQlCall.
 */
export const fulfill = (requestContext: RequestContext) => {
  if (!queue.length) return Promise.resolve();
  return makeGraphQlCall(requestContext, queue).then(
    ([results, extensions]) => fulfillData(results, extensions),
    err => {
      console.error(`ssrFetchExchange error from makeGraphQlCall:`, err);
    },
  );
};

export const fetchCacheData = () => cache;

export const getQueuedRequests = (): $ReadOnlyArray<Operation> => queue;
